import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import QRCode from 'qrcode';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { buildPublicFormUrl, resolvePublicFormsBaseUrl } from '../public-forms/public-form-url.util';

@Component({
  standalone: true,
  selector: 'ain-dynamic-form-share-panel',
  imports: [ButtonModule, InputTextModule, FormsModule, InfoHintComponent, ToastModule],
  providers: [MessageService],
  template: `
    <p-toast />
    <section class="share-panel">
      <ain-info-hint
        [titleKey]="'dynamic.public.share.title'"
        [descriptionKey]="'dynamic.public.share.description'"
        [bulletKeys]="[
          'dynamic.public.share.bulletUrl',
          'dynamic.public.share.bulletEmbed',
          'dynamic.public.share.bulletQr'
        ]" />

      <label>{{ t('dynamic.public.share.publicUrl') }}</label>
      <div class="copy-row">
        <input pInputText [readonly]="true" [ngModel]="publicUrl()" class="w-full" />
        <button pButton type="button" icon="pi pi-copy" [label]="t('dynamic.public.share.copy')" (click)="copy(publicUrl())"></button>
      </div>

      @if (qrDataUrl()) {
        <div class="qr-block">
          <img [src]="qrDataUrl()" [alt]="t('dynamic.public.share.qrAlt')" width="180" height="180" />
        </div>
      }

      <label>{{ t('dynamic.public.share.iframeSnippet') }}</label>
      <textarea readonly rows="4" class="snippet">{{ iframeSnippet() }}</textarea>
      <button pButton type="button" severity="secondary" icon="pi pi-copy" [label]="t('dynamic.public.share.copyIframe')" (click)="copy(iframeSnippet())"></button>

      <label>{{ t('dynamic.public.share.scriptSnippet') }}</label>
      <textarea readonly rows="5" class="snippet">{{ scriptSnippet() }}</textarea>
      <button pButton type="button" severity="secondary" icon="pi pi-copy" [label]="t('dynamic.public.share.copyScript')" (click)="copy(scriptSnippet())"></button>
    </section>
  `,
  styles: [`
    .share-panel { display: grid; gap: 12px; }
    .copy-row { display: flex; gap: 8px; align-items: center; }
    .w-full { flex: 1; }
    .snippet {
      width: 100%;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 12px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 10px;
      box-sizing: border-box;
      background: #f8fafc;
    }
    .qr-block { display: flex; justify-content: flex-start; }
  `]
})
export class DynamicFormSharePanelComponent {
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly slug = input.required<string>();

  readonly publicUrl = computed(() => buildPublicFormUrl(this.slug()));
  readonly iframeSnippet = computed(() => {
    const url = buildPublicFormUrl(this.slug(), { mode: 'inline' });
    return `<iframe src="${url}" title="Form" style="border:0;width:100%;min-height:640px;" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>`;
  });
  readonly scriptSnippet = computed(() => {
    const base = resolvePublicFormsBaseUrl();
    const loader = `${base}/assets/form-embed.js`;
    return `<div id="ain-form-${this.slug()}"></div>\n<script src="${loader}" data-ain-form-embed data-slug="${this.slug()}" data-mode="floating" data-target="#ain-form-${this.slug()}" data-base="${base}"></script>`;
  });

  readonly qrDataUrl = signal('');

  constructor() {
    effect(() => {
      const url = this.publicUrl();
      void QRCode.toDataURL(url, { margin: 1, width: 180 })
        .then((dataUrl: string) => this.qrDataUrl.set(dataUrl))
        .catch(() => this.qrDataUrl.set(''));
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  copy(value: string): void {
    void navigator.clipboard.writeText(value).then(() => {
      this.messages.add({ severity: 'success', summary: this.t('dynamic.public.share.copySuccess') });
    }).catch(() => {
      this.messages.add({ severity: 'error', summary: this.t('dynamic.public.share.copyFailed') });
    });
  }
}
